/**
 * Reading a reader-chosen local file for an all-or-nothing import. Shared by
 * every tool whose import replaces on-page state (CSV mapping profiles, the
 * trial-balance worksheet), so the size cap and decoding rules cannot drift
 * between them. Callers keep their own stale-pick counter: only they know
 * which pick is current.
 */

type LocalFileRead =
  | { ok: true; bytes: Uint8Array | string }
  | { ok: false; reason: "too-large" | "unreadable" };

/**
 * Size first, so an oversized file is never read into memory; then the raw
 * bytes, for the caller to decode with {@link decodeUtf8Strict} or
 * {@link decodeUtf8Replacing}. Falls back to `text()` where
 * `Blob.arrayBuffer` is missing.
 */
export async function readLocalFile(
  file: File,
  maxBytes: number,
): Promise<LocalFileRead> {
  if (file.size > maxBytes) return { ok: false, reason: "too-large" };
  try {
    const bytes =
      typeof file.arrayBuffer === "function"
        ? new Uint8Array(await file.arrayBuffer())
        : await file.text();
    return { ok: true, bytes };
  } catch {
    return { ok: false, reason: "unreadable" };
  }
}

/**
 * Strict UTF-8 decode with a leading BOM removed; `null` for bytes that are
 * not valid UTF-8. A string input is taken as already decoded.
 */
export function decodeUtf8Strict(input: string | Uint8Array): string | null {
  let text: string;
  if (typeof input === "string") {
    text = input;
  } else {
    try {
      text = new TextDecoder("utf-8", { fatal: true }).decode(input);
    } catch {
      return null;
    }
  }
  return text.replace(/^\uFEFF/, "");
}

/**
 * UTF-8 decode that replaces invalid bytes with U+FFFD and drops a leading
 * BOM: exactly what `Blob.text()` does. For a file that must decode the way a
 * sibling input read with `file.text()` does — the OFX converter's previous
 * export, so a Windows-1252 export the current input accepts is accepted
 * there too and the same bytes yield the same text on both sides.
 */
export function decodeUtf8Replacing(input: string | Uint8Array): string {
  return typeof input === "string" ? input : new TextDecoder("utf-8").decode(input);
}
